"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from 'next/navigation';
import { generateServiceFromPathname, shouldShowServiceDropdown, REQUIRED_SERVICE } from "@/types/constants";

interface QuoteFormHeaderProps {
  requiredService?: string;
  buttonLabel?: string;
  formTitle?: string;
}

interface QuoteFormHeaderData {
  name: string;
  email_address: string;
  company: string;
  phone_number: string;
  service: string;
  how_did_you_find_us: string;
  enquiry: string;
  marketing: boolean;
  analytics_id: string;
  "WC GCLID": string;
}

interface FormError {
  field: string;
  message: string;
}

export default function QuoteFormHeader({
  requiredService,
  buttonLabel,
  formTitle
}: QuoteFormHeaderProps) {
  const [formData, setFormData] = useState<QuoteFormHeaderData>({
    name: "",
    email_address: "",
    company: "",
    phone_number: "",
    service: "",
    how_did_you_find_us: "",
    enquiry: "",
    marketing: false,
    analytics_id: "GTM-TFKVPCV",
    "WC GCLID": "",
  });

  const [errors, setErrors] = useState<FormError[]>([]);
  const [submissionError, setSubmissionError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const pathname = usePathname();
  const currentService = generateServiceFromPathname(pathname);
  const showServiceDropdown = shouldShowServiceDropdown(pathname);
  const [csrfToken, setCsrfToken] = useState<string>('');

  useEffect(() => {
    const analyticsId = localStorage.getItem("_ga");
    setFormData(prev => ({
      ...prev,
      analytics_id: analyticsId || "",
      service: currentService === null ? "" : currentService
    }));
  }, [currentService]);


  useEffect(() => {
    const getGclidParam = () => {
      if (typeof window === 'undefined') return null;
      const match = RegExp('[?&]gclid=([^&]*)').exec(window.location.search);
      return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
    };

    const shouldStoreGclid = () => {
      const gclsrc = new URLSearchParams(window.location.search).get('gclsrc');
      return !gclsrc || gclsrc.indexOf('aw') !== -1;
    };

    const gclidParam = getGclidParam();

    if (gclidParam && shouldStoreGclid()) {
      const expiryDate = new Date().getTime() + (90 * 24 * 60 * 60 * 1000);
      const gclidData = {
        value: gclidParam,
        expiryDate: expiryDate
      };
      localStorage.setItem('gclid', JSON.stringify(gclidData));

      setFormData(prev => ({
        ...prev,
        "WC GCLID": gclidParam
      }));
    } else {
      const storedGclid = localStorage.getItem('gclid');
      if (storedGclid) {
        const gclidData = JSON.parse(storedGclid);
        if (new Date().getTime() < gclidData.expiryDate) {
          setFormData(prev => ({
            ...prev,
            "WC GCLID": gclidData.value
          }));
        } else {
          localStorage.removeItem('gclid');
        }
      }
    }
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const inputValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: inputValue,
    }));
  };

  useEffect(() => {
    fetch('/api/csrf')
      .then(res => {
        const token = res.headers.get('x-csrf-token');
        if (token) {
          setCsrfToken(token);
        }
      })
      .catch(console.error);
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSubmissionError("");
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/quote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          ...formData,
          form_name: "quote",
          form_url: window.location.href,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          setErrors(result.errors);
        } else {
          setSubmissionError("An error occurred while submitting the form.");
        }
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'form1',
        'enhanced_conversion_data': {
          'email': formData.email_address,
          'phone_number': formData.phone_number || ''
        }
      });

      setIsSubmitted(true);
      setFormData({
        name: "",
        email_address: "",
        company: "",
        phone_number: "",
        service: requiredService || "",
        how_did_you_find_us: "",
        enquiry: "",
        marketing: false,
        analytics_id: "GTM-TFKVPCV",
        "WC GCLID": "",
      });

    } catch (error) {
      console.error('Submit error:', error);
      setSubmissionError("An error occurred while submitting the form.");
    } finally {
      setIsSubmitting(false);
    }
  };


  if (isSubmitted) {
    return (
      <div className="card card-lighter p-3 p-md-4" style={{ margin: "-50px 0" }}>
        <div className="alert alert-success">
          <h4>Thank you for your enquiry</h4>
          <p>We&apos;ll be in touch shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-lighter p-3 p-md-4" style={{ margin: "-50px 0" }}>
      <h2 className="h3 mb-3 text-center">
        {formTitle}
      </h2>

      {errors.length > 0 && (
        <div className="alert alert-danger w-100">
          {errors.map((err, i) => (
            <p className="mb-0" key={i}>
              {err.message}
            </p>
          ))}
        </div>
      )}

      {submissionError && (
        <div className="alert alert-danger w-100">
          <p className="mb-0">{submissionError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="fast-quote">
        <input
          type="hidden"
          name="how_did_you_find_us"
          value={formData.how_did_you_find_us}
        />
        <input type="hidden" name="anchor" value="#quickQuote" />
        <div className="row gy-2 mb-2 gx-2">
          <div className="col-24 col-md-12">
            <input
              type="text"
              name="name"
              className="form-control"
              placeholder="Your name *"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="col-24 col-md-12">
            <input
              type="email"
              name="email_address"
              className="form-control"
              placeholder="Email address *"
              value={formData.email_address}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="col-24 col-md-12">
            <input
              type="text"
              name="company"
              className="form-control"
              placeholder="Company name *"
              value={formData.company}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="col-24 col-md-12">
            <input
              type="tel"
              name="phone_number"
              className="form-control"
              placeholder="Telephone"
              value={formData.phone_number}
              onChange={handleInputChange}
            />
          </div>
          {showServiceDropdown ? (
            <div className="col-24">
              <select
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                className="form-control"
                required
                aria-label="Required service"
                aria-required="true"
                aria-invalid={formData.service === ''}
              >
                <option value="">Required service</option>
                {REQUIRED_SERVICE.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="d-none">
              <input
                type="hidden"
                name="service"
                value={formData.service}
              />
            </div>
          )}
          <div className="col-24">
            <textarea
              name="enquiry"
              rows={3}
              maxLength={1500}
              className="form-control"
              placeholder="Comments *"
              value={formData.enquiry}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="col-24">
            <label className="small text-dark">
              <input
                type="checkbox"
                name="marketing"
                checked={formData.marketing}
                onChange={handleInputChange}
                className="me-2"
              />
              I&#39;d like to receive free cyber and compliance resources, and
              stay up-to-date with Bulletproof services.{" "}
              <Link href="/privacy-notice" target="_blank">
                Privacy policy
              </Link>
            </label>
          </div>
        </div>
        <input
          type="hidden"
          name="analytics_id"
          value={formData.analytics_id}
          className="analytics_id"
        />
        <input
          type="hidden"
          id="gclid_field"
          name="WC GCLID"
          value={formData["WC GCLID"]}
        />
        <div className="text-center">
          <button
            type="submit"
            className="btn btn-md btn-secondary d-block w-100"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : buttonLabel}
          </button>
        </div>
      </form>
    </div>
  );
}